import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { SERVICES_DATA } from '@/lib/index';
import { Loader2 } from 'lucide-react';

const contactSchema = z.object({
  nom: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  email: z.string().email('Adresse courriel invalide'),
  telephone: z.string().min(10, 'Numéro de téléphone invalide'),
  typeDemande: z.enum(['parent', 'avocat', 'professionnel'], {
    required_error: 'Veuillez sélectionner un type de demande',
  }),
  serviceConcerne: z.string().min(1, 'Veuillez sélectionner un service'),
  message: z.string().min(20, 'Le message doit contenir au moins 20 caractères'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const typeDemande = watch('typeDemande');
  const serviceConcerne = watch('serviceConcerne');

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      console.log('Form data:', data);
      toast({
        title: 'Message envoyé avec succès',
        description: 'Nous vous contacterons dans les plus brefs délais.',
      });
      reset();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="nom" className="text-sm font-medium text-foreground">
          Nom complet <span className="text-destructive">*</span>
        </Label>
        <Input
          id="nom"
          {...register('nom')}
          placeholder="Votre nom"
          className="w-full"
          aria-invalid={errors.nom ? 'true' : 'false'}
        />
        {errors.nom && (
          <p className="text-sm text-destructive mt-1">{errors.nom.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium text-foreground">
          Adresse courriel <span className="text-destructive">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          {...register('email')}
          placeholder="votre@courriel.com"
          className="w-full"
          aria-invalid={errors.email ? 'true' : 'false'}
        />
        {errors.email && (
          <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="telephone" className="text-sm font-medium text-foreground">
          Téléphone <span className="text-destructive">*</span>
        </Label>
        <Input
          id="telephone"
          type="tel"
          {...register('telephone')}
          placeholder="(819) 555-0123"
          className="w-full"
          aria-invalid={errors.telephone ? 'true' : 'false'}
        />
        {errors.telephone && (
          <p className="text-sm text-destructive mt-1">{errors.telephone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="typeDemande" className="text-sm font-medium text-foreground">
          Type de demande <span className="text-destructive">*</span>
        </Label>
        <Select
          value={typeDemande || 'none'}
          onValueChange={(value) => {
            if (value !== 'none') {
              setValue('typeDemande', value as 'parent' | 'avocat' | 'professionnel');
            }
          }}
        >
          <SelectTrigger className="w-full" aria-invalid={errors.typeDemande ? 'true' : 'false'}>
            <SelectValue placeholder="Sélectionnez votre profil" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sélectionnez votre profil</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
            <SelectItem value="avocat">Avocat</SelectItem>
            <SelectItem value="professionnel">Professionnel du réseau</SelectItem>
          </SelectContent>
        </Select>
        {errors.typeDemande && (
          <p className="text-sm text-destructive mt-1">{errors.typeDemande.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="serviceConcerne" className="text-sm font-medium text-foreground">
          Service concerné <span className="text-destructive">*</span>
        </Label>
        <Select
          value={serviceConcerne || 'none'}
          onValueChange={(value) => setValue('serviceConcerne', value === 'none' ? '' : value)}
        >
          <SelectTrigger className="w-full" aria-invalid={errors.serviceConcerne ? 'true' : 'false'}>
            <SelectValue placeholder="Sélectionnez un service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sélectionnez un service</SelectItem>
            {SERVICES_DATA.map((service) => (
              <SelectItem key={service.id} value={service.id}>
                {service.titre}
              </SelectItem>
            ))}
            <SelectItem value="autre">Autre / Information générale</SelectItem>
          </SelectContent>
        </Select>
        {errors.serviceConcerne && (
          <p className="text-sm text-destructive mt-1">{errors.serviceConcerne.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-medium text-foreground">
          Message <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="message"
          {...register('message')}
          placeholder="Décrivez brièvement votre situation et vos besoins..."
          className="w-full min-h-[150px] resize-y"
          aria-invalid={errors.message ? 'true' : 'false'}
        />
        {errors.message && (
          <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
        )}
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 h-12 text-base font-medium"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            'Envoyer la demande'
          )}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground text-center mt-4">
        Vos informations sont confidentielles et ne seront utilisées que pour répondre à votre demande.
      </p>
    </form>
  );
}
